import type { TaskStatus } from 'veritone-types';

import { TDOId } from '../../universal/models/Brands';

export const namespace = 'vtn-redact-engine-speaker-separation';

export interface JobState {
  jobId?: string;
  isRunning: boolean;
  /**
   * Taken from the schema rather than written out by hand, and `TaskStatus`
   * rather than `JobStatus` — this duck polls BOTH `job(id:)` and, since
   * VE-30784, `task(id:)`, and `TaskStatus` covers both: the schema types
   * `Job.status` as `TaskStatus` too ("overall job status, as computed from
   * the statuses of its component tasks").
   *
   * It is the wider of the two — 12 values against 6 — and the extra ones are
   * real states a task reports: `aborted`, `paused`, `standby_pending`,
   * `waiting`, `resuming`, `accepted`. Narrowing this to `JobStatus` is what
   * made `aborted` unreachable in FAILED_STATUSES below, and a type that
   * disagrees with the API cannot be caught by tsc.
   */
  status: `${TaskStatus}`;
  startedOn: Date;
  endedOn?: Date;
  error?: string;
}

interface TDOJobState {
  [tdoId: TDOId]: {
    [jobId: string]: JobState;
  };
}

export type JobStore = Partial<TDOJobState>;

export const defaultState: JobStore = {};

/**
 * Terminal failures. `aborted` is only reachable on a task, so it was absent
 * while this duck polled `job(id:)` alone — leaving an aborted run neither
 * complete nor failed, so the poller spun to MAX_POLL_DURATION_MS and the
 * audio tab stayed on "in progress" for an hour (VE-30784). Transcription and
 * voice mask have always carried it.
 *
 * The remaining non-terminal TaskStatus values (`paused`, `standby_pending`,
 * `waiting`, `resuming`) deliberately stay out: a run can legitimately sit in
 * them and resume. MAX_POLL_DURATION_MS is what bounds those.
 */
export const FAILED_STATUSES: ReadonlyArray<JobState['status']> = [
  'failed',
  'cancelled',
  'aborted',
];

export const defaultJobSlice: () => JobState = () => ({
  jobId: undefined,
  isRunning: true,
  status: 'pending',
  startedOn: new Date(),
  endedOn: undefined,
  error: undefined,
});
